import React, { useState, useMemo, useCallback } from 'react';
import {
  CheckCircle,
  Plus,
  ArrowLeft,
  ArrowRight,
  Layers,
  BookOpen,
  Building2,
  AlertCircle,
  Info,
  Sparkles,
} from 'lucide-react';
import { extractEntityCode } from '@valutten/shared-utils';
import type { ScoutedFile } from '../../utils/gmailScout';
import { useCompanyOffices } from '../../hooks/useCompanyOffices';
import { LoanBookCard, LoanBookGroup } from './LoanBookCard';

// Entity code extraction comes from the one shared implementation. The inline
// copy that used to live here never received the 2026-08-28 dot fix, so a
// dotted anchor such as `MAVI1.00 AGMT ...` grouped as "no entity" in the
// wizard while the ingestion path resolved it to MAVI100.

function fileKey(f: ScoutedFile): string {
  return `${f.messageId}_${f.attachmentId}`;
}

export interface LoanBookAssignment {
  file: ScoutedFile;
  loanBookGroupKey: string;
  subscriberLoanBookName: string;
  subscriberOfficeId: string | null;
  subscriberOfficeName: string | null;
}

interface LoanBookWizardProps {
  scoutedFiles: ScoutedFile[];
  companyId: string;
  /** Prior loan book names and office assignments from previous scans (keyed by entityCode or loanBookGroupKey) */
  priorGroupNames?: Map<string, { name: string; officeId: string; officeName: string }>;
  onImport: (assignments: LoanBookAssignment[], selectedFileKeys: Set<string>) => void;
  onCancel: () => void;
}

type WizardStep = 'organize' | 'review';

export const LoanBookWizard: React.FC<LoanBookWizardProps> = ({
  scoutedFiles,
  companyId,
  priorGroupNames,
  onImport,
  onCancel,
}) => {
  const { offices, createOffice } = useCompanyOffices(companyId);
  const [step, setStep] = useState<WizardStep>('organize');

  // Initialize groups from scoutedFiles
  const [groups, setGroups] = useState<LoanBookGroup[]>(() => {
    const groupMap = new Map<string, ScoutedFile[]>();
    const unmatched: ScoutedFile[] = [];

    for (const file of scoutedFiles) {
      const code = file.entityCode || extractEntityCode(file.filename);
      if (code) {
        const existing = groupMap.get(code) || [];
        existing.push(file);
        groupMap.set(code, existing);
      } else {
        unmatched.push(file);
      }
    }

    const result: LoanBookGroup[] = [];
    const sortedCodes = Array.from(groupMap.keys()).sort();

    for (const code of sortedCodes) {
      const prior = priorGroupNames?.get(code);
      const headOffice = offices.length === 1 ? offices[0] : null;
      // Prefer prior name > entityName from filename > raw entity code
      const filesForCode = groupMap.get(code)!;
      const friendlyName = filesForCode.find((f) => f.entityName)?.entityName;
      result.push({
        id: code,
        entityCode: code,
        loanBookName: prior?.name || friendlyName || code,
        officeId: prior?.officeId || headOffice?.id || null,
        officeName: prior?.officeName || headOffice?.name || null,
        files: filesForCode,
        isExpanded: false,
      });
    }

    if (unmatched.length > 0) {
      result.push({
        id: '__unmatched__',
        entityCode: null,
        loanBookName: 'Unmatched Files',
        officeId: null,
        officeName: null,
        files: unmatched,
        isExpanded: true,
      });
    }

    return result;
  });

  const [selectedFiles] = useState<Set<string>>(
    () => new Set(scoutedFiles.map(fileKey))
  );

  // Derived state
  const unmatchedGroup = groups.find((g) => g.id === '__unmatched__');
  const namedGroups = groups.filter((g) => g.id !== '__unmatched__');
  const hasUnmatchedFiles = (unmatchedGroup?.files.length || 0) > 0;

  const canProceed = useMemo(() => {
    if (hasUnmatchedFiles) return false;
    if (namedGroups.length === 0) return false;
    return namedGroups.every((g) => g.loanBookName.trim() && g.officeId);
  }, [namedGroups, hasUnmatchedFiles]);

  const totalSelectedFiles = useMemo(() => {
    let count = 0;
    for (const g of groups) {
      for (const f of g.files) {
        if (selectedFiles.has(fileKey(f))) count++;
      }
    }
    return count;
  }, [groups, selectedFiles]);

  const uniqueOfficeCount = useMemo(() => {
    return new Set(namedGroups.map((g) => g.officeId).filter(Boolean)).size;
  }, [namedGroups]);

  // Group operations
  const updateGroupName = useCallback((groupId: string, name: string) => {
    setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, loanBookName: name } : g));
  }, []);

  const updateGroupOffice = useCallback((groupId: string, officeId: string, officeName: string) => {
    setGroups((prev) =>
      prev.map((g) => g.id === groupId ? { ...g, officeId, officeName } : g)
    );
  }, []);

  const toggleGroupExpand = useCallback((groupId: string) => {
    setGroups((prev) =>
      prev.map((g) => g.id === groupId ? { ...g, isExpanded: !g.isExpanded } : g)
    );
  }, []);

  const moveFile = useCallback((fKey: string, targetGroupId: string) => {
    setGroups((prev) => {
      let movedFile: ScoutedFile | null = null;
      const withoutFile = prev.map((g) => {
        const idx = g.files.findIndex((f) => fileKey(f) === fKey);
        if (idx >= 0) {
          movedFile = g.files[idx];
          return { ...g, files: [...g.files.slice(0, idx), ...g.files.slice(idx + 1)] };
        }
        return g;
      });
      if (!movedFile) return prev;
      return withoutFile.map((g) =>
        g.id === targetGroupId ? { ...g, files: [...g.files, movedFile!] } : g
      ).filter((g) => g.files.length > 0 || g.id === '__unmatched__');
    });
  }, []);

  const createNewGroup = useCallback(() => {
    const id = `manual_${Date.now()}`;
    setGroups((prev) => [
      ...prev.filter((g) => g.id !== '__unmatched__'),
      {
        id,
        entityCode: null,
        loanBookName: '',
        officeId: null,
        officeName: null,
        files: [],
        isExpanded: true,
      },
      ...prev.filter((g) => g.id === '__unmatched__'),
    ]);
  }, []);

  const handleImport = useCallback(() => {
    const assignments: LoanBookAssignment[] = [];
    for (const group of groups) {
      if (group.id === '__unmatched__') continue;
      for (const file of group.files) {
        if (!selectedFiles.has(fileKey(file))) continue;
        assignments.push({
          file,
          loanBookGroupKey: group.id,
          subscriberLoanBookName: group.loanBookName,
          subscriberOfficeId: group.officeId,
          subscriberOfficeName: group.officeName,
        });
      }
    }
    onImport(assignments, selectedFiles);
  }, [groups, selectedFiles, onImport]);

  // ─── STEP 1: ORGANIZE ──────────────────────────────────────────────
  if (step === 'organize') {
    return (
      <div>
        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
              1
            </div>
            <span className="text-sm font-medium text-white">Organize</span>
          </div>
          <div className="h-px flex-1 bg-gray-700" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-400">
              2
            </div>
            <span className="text-sm text-gray-500">Review & Import</span>
          </div>
        </div>

        {/* Header with instructions */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-5 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Layers size={20} className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                Organize Your Loan Books
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                {namedGroups.length > 0
                  ? `We found ${scoutedFiles.length} commission files and automatically grouped them into ${namedGroups.length} loan ${namedGroups.length === 1 ? 'book' : 'books'} based on the file prefixes.`
                  : `We found ${scoutedFiles.length} commission files. Create loan books to organize them.`}
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-4 bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-300 space-y-1.5">
                <p><strong className="text-white">For each loan book below:</strong></p>
                <ol className="list-decimal list-inside space-y-1 text-gray-400">
                  <li><strong className="text-gray-300">Name it</strong> — use the name your team recognises (e.g. "Sydney Office Loans", "Queensland Book")</li>
                  <li><strong className="text-gray-300">Assign an office</strong> — select which office or branch this loan book belongs to, or create a new one</li>
                  <li><strong className="text-gray-300">Check the files</strong> — expand each card to verify the right files are in the right group</li>
                </ol>
                {hasUnmatchedFiles && (
                  <p className="text-amber-400 mt-2">
                    Some files couldn't be auto-grouped. Assign them to a loan book below before continuing.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Loan book cards */}
        <div className="space-y-3 mb-4">
          {namedGroups.map((group, index) => (
            <LoanBookCard
              key={group.id}
              group={group}
              index={index}
              offices={offices}
              allGroups={[...namedGroups, ...(unmatchedGroup ? [unmatchedGroup] : [])]}
              onUpdateName={(name) => updateGroupName(group.id, name)}
              onUpdateOffice={(oid, oname) => updateGroupOffice(group.id, oid, oname)}
              onMoveFile={(fk, target) => moveFile(fk, target)}
              onToggleExpand={() => toggleGroupExpand(group.id)}
              onCreateOffice={createOffice}
            />
          ))}
        </div>

        {/* Unmatched files */}
        {hasUnmatchedFiles && (
          <div className="border border-amber-500/30 rounded-2xl bg-amber-900/10 overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-amber-500/20">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-400" />
                <span className="text-sm font-medium text-amber-300">
                  Unmatched Files ({unmatchedGroup!.files.length})
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                These files couldn't be automatically grouped. Use the dropdown on each file to assign it to a loan book above, or create a new loan book.
              </p>
            </div>
            <div className="divide-y divide-amber-500/10">
              {unmatchedGroup!.files.map((file) => (
                <div
                  key={fileKey(file)}
                  className="flex items-center gap-2.5 px-5 py-2.5 text-sm"
                >
                  <span className="text-gray-300 truncate flex-1">{file.filename}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) moveFile(fileKey(file), e.target.value);
                    }}
                    className="w-40 px-2.5 py-1.5 bg-gray-900/60 border border-amber-500/30 rounded-lg text-xs text-gray-200 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Assign to loan book...</option>
                    {namedGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.loanBookName || g.entityCode || 'Unnamed'}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={createNewGroup}
              className="px-4 py-2 text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1.5 border border-gray-700 rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <Plus size={14} /> New Loan Book
            </button>
          </div>
          <div className="flex items-center gap-3">
            {!canProceed && namedGroups.length > 0 && (
              <p className="text-xs text-amber-400">
                {hasUnmatchedFiles
                  ? 'Assign all files first'
                  : 'Name + office needed for each'}
              </p>
            )}
            <button
              onClick={() => setStep('review')}
              disabled={!canProceed}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
            >
              Next: Review
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2: REVIEW & IMPORT ────────────────────────────────────────
  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle size={14} className="text-green-400" />
          </div>
          <span className="text-sm text-green-400 font-medium">Organized</span>
        </div>
        <div className="h-px flex-1 bg-green-500/30" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
            2
          </div>
          <span className="text-sm font-medium text-white">Review & Import</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-5 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <Sparkles size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">
              Ready to Import
            </h3>
            <p className="text-sm text-gray-400">
              Review your loan book setup below. After importing, our team will review your files and assign the correct data template to each loan book.
            </p>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <BookOpen size={16} className="text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-white">{namedGroups.length}</p>
          <p className="text-xs text-gray-400">Loan Books</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <Layers size={16} className="text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-white">{totalSelectedFiles}</p>
          <p className="text-xs text-gray-400">Files</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <Building2 size={16} className="text-violet-400" />
          </div>
          <p className="text-2xl font-bold text-white">{uniqueOfficeCount}</p>
          <p className="text-xs text-gray-400">Offices</p>
        </div>
      </div>

      {/* Loan book summary cards */}
      <div className="space-y-2 mb-6">
        {namedGroups.map((group) => (
          <div
            key={group.id}
            className="flex items-center gap-3 bg-gray-800/40 border border-gray-700 rounded-xl px-4 py-3"
          >
            <CheckCircle size={18} className="text-green-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {group.loanBookName}
              </p>
              <p className="text-xs text-gray-400">
                {group.officeName || 'No office'} — {group.files.length} files
              </p>
            </div>
            {group.entityCode && (
              <span className="px-2 py-0.5 bg-blue-500/15 text-blue-300 text-xs font-mono rounded-full">
                {group.entityCode}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* What happens next */}
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl px-4 py-3 mb-6">
        <p className="text-xs font-medium text-gray-300 mb-1.5">What happens next?</p>
        <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
          <li>Your files will be securely uploaded to Valutten</li>
          <li>Our team reviews your loan books and assigns the correct data template</li>
          <li>Your dashboard will be built automatically once processing is complete</li>
        </ol>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep('organize')}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} /> Back to Edit
        </button>
        <button
          onClick={handleImport}
          className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg shadow-green-500/20"
        >
          Import {totalSelectedFiles} Files
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};
